import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Mail, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import Button from '../common/Button';

interface ScheduleExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ScheduleExportModal: React.FC<ScheduleExportModalProps> = ({ isOpen, onClose }) => {
  const [frequency, setFrequency] = useState<'weekly' | 'monthly'>('monthly');
  const [dayOfWeek, setDayOfWeek] = useState('monday');
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [time, setTime] = useState('09:00');
  const [isScheduling, setIsScheduling] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);

  const handleSchedule = async () => {
    setIsScheduling(true);
    
    // Simulate API call to schedule export
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsScheduling(false);
    setIsScheduled(true);
    
    // Auto close after 3 seconds
    setTimeout(() => {
      setIsScheduled(false);
      onClose();
    }, 3000);
  };

  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const daysOfMonth = Array.from({ length: 28 }, (_, i) => ({
    value: (i + 1).toString(),
    label: `${i + 1}${i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}`
  }));

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="bg-primary-100 p-2 rounded-lg">
                    <Calendar className="w-6 h-6 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Schedule Export</h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-gray-600 mt-2">
                Automatically send export data to admin@lsem.org.ng
              </p>
            </div>

            {isScheduled ? (
              /* Success State */
              <div className="p-6 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="bg-success-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                >
                  <CheckCircle className="w-8 h-8 text-success-600" />
                </motion.div>
                <h4 className="text-lg font-bold text-gray-800 mb-2">Export Scheduled!</h4>
                <p className="text-gray-600">
                  Your {frequency} export has been scheduled successfully. 
                  Reports will be automatically sent to admin@lsem.org.ng.
                </p>
              </div>
            ) : (
              /* Configuration Form */
              <div className="p-6 space-y-6">
                {/* Frequency Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Export Frequency
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setFrequency('weekly')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        frequency === 'weekly'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-semibold">Weekly</div>
                        <div className="text-sm opacity-75">Every week</div>
                      </div>
                    </button>
                    <button
                      onClick={() => setFrequency('monthly')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        frequency === 'monthly'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-semibold">Monthly</div>
                        <div className="text-sm opacity-75">Every month</div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Day Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {frequency === 'weekly' ? 'Day of Week' : 'Day of Month'}
                  </label>
                  <select
                    value={frequency === 'weekly' ? dayOfWeek : dayOfMonth}
                    onChange={(e) => frequency === 'weekly' ? setDayOfWeek(e.target.value) : setDayOfMonth(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  >
                    {(frequency === 'weekly' ? daysOfWeek : daysOfMonth).map(day => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Time Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    <Clock className="w-4 h-4 inline mr-2" />
                    Time
                  </label>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                </div>

                {/* Email Destination */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-semibold text-gray-700">Email Destination</span>
                  </div>
                  <p className="text-gray-600 text-sm">admin@lsem.org.ng</p>
                </div>

                {/* Schedule Summary */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-primary-800 mb-1">Schedule Summary</h4>
                      <p className="text-sm text-primary-700">
                        Export data will be sent {frequency} on{' '}
                        {frequency === 'weekly' 
                          ? daysOfWeek.find(d => d.value === dayOfWeek)?.label
                          : `the ${daysOfMonth.find(d => d.value === dayOfMonth)?.label}`
                        } at {time} to admin@lsem.org.ng
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="flex-1"
                    disabled={isScheduling}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSchedule}
                    className="flex-1"
                    disabled={isScheduling}
                  >
                    {isScheduling ? 'Scheduling...' : 'Schedule Export'}
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScheduleExportModal;